import $ from 'wee-dom';
import $events from 'wee-events';
import $mediator from 'wee-mediator';
import { _html } from 'core/variables';
import Tabbable from '../../../scripts/tabbable';
import KeyHelper from '../../../scripts/keyhelper';

export default class {
    constructor() {
        /**
         * The parent for the panels, we use this for adding scope
         * to our selections
         * @member {string}
         */
        this.parent = ':navigationDesktopPanels';

        /**
         * The main navigation element
         * @member {$}
         */
        this.$el = $(':navigationDesktopPanels');

        /**
         * The navigation links
         * @member {$}
         */
        this.$links = $(':navigationDesktopLink');

        /**
         * Nav option panels
         * @member {$}
         */
        this.$panels = $(':navigationPanel', this.parent);

        /**
         * Wee selection of the HTML element
         * @member {$}
         */
        this.$html = $(_html);

        /**
         * The navigation section of the site header
         * @member {$}
         */
        this.$siteHeader = $(':siteHeaderMain');

        /**
         * Bottom action links
         * @member {$}
         */
        this.$bottomActions = $(':navigationPanelBottomAction');

        /**
         * The close button
         * @member {$}
         */
        this.$closeButton = $(':navigationPanelClose');

        /**
         * The currently open panel
         * @member {$}
         */
        this.$activePanel = null;

        /**
         * The internal open state
         * @member {boolean}
         */
        this.isOpen = false;

        /**
         * The current active panel index
         * @member {boolean}
         */
        this.activeIndex = -1;

        /**
         * The internal reference to the tabbable instance
         * @member {null|Tabbable}
         */
        this.tabbable = null;

        // Add the link class to the bottom buttons
        // and add the arrow
        this.$bottomActions
            .addClass('-link')
            .text((i, text) => `${text} >`);

        // Close navigation panels when search is opened
        $mediator.on('search.desktop.operate', () => {
            if (this.isOpen) {
                this.close();
            }
        });
    }

    /**
     * Open the navigation
     *
     * @return {this}
     */
    open() {
        this.operate();

        return this;
    }

    /**
     * Close the navigation
     *
     * @return {this}
     */
    close() {
        this.operate(false);

        this.$links.eq(this.activeIndex)[0].focus();

        $mediator.emit('navigation.close');

        return this;
    }

    /**
     * Toggle the navigation
     *
     * @return {this}
     */
    toggle() {
        this.operate(! this.isOpen);

        return this;
    }

    /**
     * Operate the navigation
     *
     * @param {boolean} state - The current 'open' state of the navigation
     */
    operate(state = true) {
        this.$el[state ? 'activate' : 'deactivate']();
        this.$html[state ? 'addClass' : 'removeClass']('-has-navigation');
        this.isOpen = state;
        this.$siteHeader[state ? 'activate' : 'deactivate']();

        $mediator.emit('navigation.operate', state);

        if (! state) {
            this.$activePanel = null;
            this.tabbable = null;
            $events.off(null, '.navigationPanel');
        }
    }

    openPanel(panel) {
        if (! this.isOpen) {
            this.open();
        }

        this.$panels.each(item => $(item).disable().ariaHidden());
        this.$links.aria('selected', false);

        for (let i = 0; i < this.$panels.length; i += 1) {
            const $panel = $(this.$panels[i]);

            if ($panel.data('type') === panel) {
                $panel.enable()
                    .ariaHidden(false);

                // Set the current active panel
                this.$activePanel = $panel;

                this.$links.eq(i).aria('selected', true);

                // Grab the current tabbed item for use later
                this.lastTabbed = document.activeElement;

                // Create a new instance of the tabbable
                // class to trap the tab focus
                this.tabbable = new Tabbable({
                    el: $panel,
                    callback: () => {
                        // Select the next link once menu is tabbed out of
                        this.close();
                        this.$links.eq(i + 1)[0].focus();
                    },
                });

                $events.on({
                    body: {
                        keydown: (e, el) => {
                            if (KeyHelper.isShiftTab(e) && e.target === this.tabbable.$tabbableEls[0]) {
                                this.$links.eq(i)[0].focus();

                                e.preventDefault();
                            }
                        },
                    },
                    [this.$links.sel]: {
                        keydown: (e, el) => {
                            if (this.isOpen && KeyHelper.isTab(e)) {
                                // Focus first element in the panel
                                this.tabbable.$firstEl[0].focus();

                                e.preventDefault();
                            }
                        }
                    },
                }, {
                    namespace: 'navigationPanel',
                });

                // Once a panel is open, bind the next tab key press to
                // the first tabbable element inside the active panel
                $events.on({
                    body: {
                        keydown: (e, el) => {
                            if (this.isOpen && KeyHelper.isTab(e)) {
                                // Focus first element in the panel
                                this.tabbable.$firstEl[0].focus();

                                e.preventDefault();
                            }
                        },
                    },
                }, {
                    namespace: 'navigationPanel',
                    once: true,
                });

                // Set the active index
                this.activeIndex = i;

                break;
            }
        }
    }

    /**
     * Toggle the next panel
     */
    nextPanel() {
        const next = this.activeIndex + 1;

        if (next < this.$links.length) {
            this.togglePanel(this.$links.eq(this.activeIndex + 1).data('type'));
            this.$links.eq(next)[0].focus();
        }
    }

    /**
     * Toggle the previous panel
     */
    prevPanel() {
        const previous = this.activeIndex - 1;

        if (previous > -1) {
            this.togglePanel(this.$links.eq(this.activeIndex - 1).data('type'));
            this.$links.eq(previous)[0].focus();
        }
    }

    /**
     * Toggle a panel
     *
     * @param {string} panel - The panel slug
     */
    togglePanel(panel) {
        if (this.isOpen && this.$activePanel.data('type') === panel) {
            this.close();
        } else {
            this.openPanel(panel);
        }

        $mediator.emit('navigation.panel.toggled');
    }

    /**
     * Destroy Method
     */
    destroy() {
        this.close();

        // Remove the link class from the bottom buttons
        // and remove the arrow
        this.$bottomActions
            .removeClass('-link')
            .text((i, text) => {
                return text.replace(' >', '');
            });

        $events.off(null, '.navigationPanel');
        this.tabbable = null;
        this.$activePanel = null;
    }
}
